import { Alert, createModal, Icon, Input, Select, Stack } from '@tf/ui';
import { splitObjectId, T } from '@tf/utils';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { creatableClientCaseKindOptions } from '@/components/cases/requestKind';
import { ModalFooter } from '@/components/shared';
import { useCreateCaseMutation } from '@/core/api/caseRequests';
import { useSelectedAccount } from '@/core/hooks';
import type { CaseKind } from '@/core/types/cases';
import { paths } from '@/routes/paths';

import { extractUniqueIndividualEntities, isValidEmail } from './utils';

type FieldsState = {
	email: string;
	type: CaseKind;
	typeError: string;
	emailError: string;
	graphId: string;
	graphIdError: string;
};

const initialFieldsState = {
	email: '',
	emailError: '',
	type: 'information_request' as const,
	typeError: '',
	graphId: '',
	graphIdError: '',
};

interface CreateCaseModalProps {
	close: () => void;
}

export const CreateCaseModal = createModal<CreateCaseModalProps>(({ close }) => {
	const createClientCaseMutation = useCreateCaseMutation();
	const navigate = useNavigate();
	const { profileId, meta } = useSelectedAccount();
	const [fieldsState, setFieldsState] = useState<FieldsState>(initialFieldsState);

	const { reviews } = useSelectedAccount();
	const applicantReview = reviews.find((r) => r.connectionKind === 'APPLICANT');
	T.assertNotNullish(applicantReview, 'no applicant review found');

	const individualEntities = extractUniqueIndividualEntities(reviews);

	useEffect(() => {
		if (individualEntities.length === 1) {
			setFieldsState((prevState) => ({
				...prevState,
				graphId: String(individualEntities[0].graphNodeId),
			}));
		}
	}, [individualEntities.length, individualEntities[0]?.graphNodeId]);

	return (
		<form
			onSubmit={async (e) => {
				e.preventDefault();

				if (!fieldsState.type) {
					setFieldsState((prevState) => ({ ...prevState, typeError: 'Empty type' }));
				}

				if (!isValidEmail(fieldsState.email)) {
					setFieldsState((prevState) => ({ ...prevState, emailError: 'Invalid email' }));
				}

				if (fieldsState.type === 'identity_verification' && !fieldsState.graphId) {
					setFieldsState((prevState) => ({ ...prevState, graphIdError: 'Empty graph ID' }));
				}

				if (
					!fieldsState.type ||
					!isValidEmail(fieldsState.email) ||
					(fieldsState.type === 'identity_verification' && !fieldsState.graphId)
				) {
					return;
				}

				await createClientCaseMutation.mutateAsync(
					{
						profileId,
						unitNodeId: parseInt(fieldsState.graphId, 10),
						recipientEmail: fieldsState.email.trim(),
						requestKind: fieldsState.type,
					},
					{
						onSuccess: (res) => {
							close();
							setFieldsState(initialFieldsState);

							const { objectName } = splitObjectId(res[0].id);
							navigate(paths.case(meta.graphNodeId, objectName));
						},
					},
				);
			}}
		>
			<Stack p="1rem">
				<Select
					required
					label="Case type"
					placeholder="Select case type"
					data={creatableClientCaseKindOptions}
					value={fieldsState.type}
					error={Boolean(fieldsState.typeError)}
					onChange={(type) => {
						if (type) {
							setFieldsState((prevState) => ({ ...prevState, type: type as CaseKind, typeError: '' }));
						}
					}}
				/>

				{fieldsState.type === 'identity_verification' && (
					<Select
						required
						label="Individual entity"
						variant="default"
						placeholder="Select account"
						value={fieldsState.graphId}
						error={Boolean(fieldsState.graphIdError)}
						onChange={(graphId) => {
							if (!graphId) return;
							setFieldsState((prevState) => ({ ...prevState, graphId, graphIdError: '' }));
						}}
						data={individualEntities.map((e) => ({ value: String(e.graphNodeId), label: e.name }))}
					/>
				)}

				{fieldsState.type === 'identity_verification' && individualEntities.length === 0 && (
					<Alert
						icon={<Icon.IconAlertCircle />}
						color="dark"
						sx={{
							backgroundColor: `rgba(246, 102, 6, 0.2)`,
							borderColor: `rgba(246, 102, 6, 0.5)`,
							color: `rgb(246, 102, 6)`,
						}}
						fw="600"
					>
						Please add at least one individual entity to your account to proceed
					</Alert>
				)}

				<Input.Wrapper required label="Email">
					<Input
						error={fieldsState.emailError}
						placeholder="Enter email"
						value={fieldsState.email}
						onChange={(e) => {
							const email = e.target.value;
							setFieldsState((prevState) => ({ ...prevState, email, emailError: '' }));
						}}
					/>
				</Input.Wrapper>
			</Stack>
			<ModalFooter submitText={'Create'} isDisabled={createClientCaseMutation.isPending} onCancel={close} />
		</form>
	);
});
